/**
 * Product card quantity stepper (noon-style, hover-driven).
 *
 * Each in-stock, single-variant card carries a compact ATC slot next to its
 * Buy Now button. Hovering the slot expands it into a [- qty +] stepper;
 * moving the pointer away collapses it again a moment later.
 *
 *   - not in cart, idle   -> a cart-icon "add" button
 *   - not in cart, hover  -> [- 1 +] stepper (the "-" is disabled; "+" adds)
 *   - in cart, idle       -> a compact cart-icon badge showing the qty
 *   - in cart, hover      -> [trash|-  qty  +] stepper (trash removes at qty 1)
 *
 * Adding / removing happens on a click of "+" / "-", never on hover. The first
 * unit is added with /cart/add.js; further changes use /cart/change.js. Buy Now
 * stays visible throughout.
 *
 * Deliberately does not touch the theme's own jQuery add-to-cart code
 * (assets/theme.js). It watches for any request to a cart-mutation endpoint
 * (fetch or jQuery's XHR-based $.ajax) and re-syncs every stepper on the page
 * from a fresh /cart.js afterwards, so an add from anywhere keeps the cards in
 * step.
 */
(function () {
  'use strict';

  var CART_MUTATION_PATTERN = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var COLLAPSE_DELAY = 2000; // ms the stepper stays open (after an add, or after the pointer leaves) before collapsing

  function routeRoot() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function updateCartCountBubble(itemCount) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      var limit = 99;
      el.textContent = itemCount <= limit ? itemCount : (window.cartStrings && window.cartStrings.item_99) || limit + '+';
    });
  }

  function updateFloatingCartNudge(itemCount) {
    var nudge = document.querySelector('[data-floating-cart-nudge]');
    if (!nudge) return;
    var countEl = nudge.querySelector('[data-floating-cart-count]');
    if (itemCount > 0) {
      if (countEl) countEl.textContent = itemCount;
      nudge.hidden = false;
    } else {
      nudge.hidden = true;
    }
  }

  // The nudge floats above the store's bottom tab bar. The offset is pinned to a
  // fixed 90px by request (runtime measurement was costly and fragile). Kept as a
  // function so its existing call sites stay valid.
  var FLOATING_CART_BOTTOM_OFFSET = '90px';

  function repositionFloatingCartNudge() {
    var nudge = document.querySelector('[data-floating-cart-nudge]');
    if (!nudge) return;
    nudge.style.setProperty('--floating-cart-bottom-offset', FLOATING_CART_BOTTOM_OFFSET);
  }

  // ---------------------------------------------------------------------------
  // Slot state
  // ---------------------------------------------------------------------------
  var collapseTimers = new WeakMap(); // slot element -> timeout id
  var hovering = new WeakSet();       // slots the pointer is currently over
  var openUntil = new WeakMap();      // slot -> timestamp to hold the stepper open until
  var prevQty = {};                   // variant id -> qty at the previous sync
  var firstSyncDone = false;          // don't treat the initial page-load qty as an "add"

  // Touch devices have no hover, and a tap there can fire a synthetic mouseover
  // that would leave the stepper stuck open (the collapse timer pauses while
  // "hovering"). So only wire the hover behaviour on real pointer devices --
  // phones drive everything from taps + the hold-open window below.
  var CAN_HOVER = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);

  function allSlots() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-card-atc-slot]'));
  }

  function groupFor(slot) {
    if (!slot) return null;
    var stepper = slot.querySelector('[data-card-qty-stepper]');
    return {
      slot: slot,
      add: slot.querySelector('[data-card-atc-add]'),
      badge: slot.querySelector('[data-card-cart-badge]'),
      stepper: stepper,
      variantId: stepper && stepper.getAttribute('data-variant-id'),
      cartQty: stepper ? (parseInt(stepper.getAttribute('data-cart-qty'), 10) || 0) : 0
    };
  }

  function clearCollapse(slot) {
    var t = collapseTimers.get(slot);
    if (t) { clearTimeout(t); collapseTimers.delete(slot); }
  }

  function scheduleCollapse(group) {
    clearCollapse(group.slot);
    if (hovering.has(group.slot)) return; // still hovering -> stay open
    var id = setTimeout(function () { collapse(group); }, COLLAPSE_DELAY);
    collapseTimers.set(group.slot, id);
  }

  // Reflect a card's cart quantity onto its stepper controls + badge. When the
  // product isn't in the cart yet, the stepper previews "1" with a disabled "-",
  // and "+" performs the first add.
  function paint(group) {
    if (!group.stepper) return;
    var qty = group.cartQty;
    var valueEl = group.stepper.querySelector('[data-qty-value]');
    var decreaseBtn = group.stepper.querySelector('[data-qty-decrease]');
    var increaseBtn = group.stepper.querySelector('[data-qty-increase]');
    var badgeCount = group.badge && group.badge.querySelector('[data-badge-qty]');

    if (qty <= 0) {
      if (valueEl) valueEl.textContent = '1';
      if (decreaseBtn) {
        decreaseBtn.disabled = true;
        decreaseBtn.classList.remove('is-remove');
        var addLabel = decreaseBtn.getAttribute('data-decrease-label');
        if (addLabel) decreaseBtn.setAttribute('aria-label', addLabel);
      }
    } else {
      if (valueEl) valueEl.textContent = qty;
      if (badgeCount) badgeCount.textContent = qty;
      if (decreaseBtn) {
        decreaseBtn.disabled = false;
        // At qty 1, decreasing removes the line -- show the trash icon + label.
        var isRemove = qty === 1;
        decreaseBtn.classList.toggle('is-remove', isRemove);
        var label = isRemove ? decreaseBtn.getAttribute('data-remove-label') : decreaseBtn.getAttribute('data-decrease-label');
        if (label) decreaseBtn.setAttribute('aria-label', label);
      }
    }

    if (increaseBtn) {
      // Stop at the real stock limit instead of firing a request that Shopify
      // silently rejects (which made "+" look broken on low-stock products).
      var limited = group.stepper.getAttribute('data-inventory-limited') === 'true';
      var maxQty = parseInt(group.stepper.getAttribute('data-inventory-quantity'), 10) || 0;
      increaseBtn.disabled = qty > 0 && limited && qty >= maxQty;
    }
  }

  function showAdd(group) {
    if (group.stepper) group.stepper.hidden = true;
    if (group.badge) group.badge.hidden = true;
    if (group.add) group.add.hidden = false;
    group.slot.classList.remove('is-expanded');
    clearCollapse(group.slot);
  }

  function showCollapsed(group) {
    if (group.add) group.add.hidden = true;
    if (group.stepper) group.stepper.hidden = true;
    if (group.badge) group.badge.hidden = false;
    group.slot.classList.remove('is-expanded');
  }

  function showExpanded(group) {
    if (!group.stepper) return;
    paint(group);
    if (group.add) group.add.hidden = true;
    if (group.badge) group.badge.hidden = true;
    group.stepper.hidden = false;
    group.slot.classList.add('is-expanded');
  }

  function collapse(group) {
    clearCollapse(group.slot);
    if (group.cartQty > 0) showCollapsed(group); else showAdd(group);
  }

  // Show the stepper and hold it open for COLLAPSE_DELAY. This is what drives
  // touch devices (tap the badge / add / +- ) since they have no hover.
  function keepOpen(group) {
    if (!group || !group.stepper) return;
    showExpanded(group);
    openUntil.set(group.slot, Date.now() + COLLAPSE_DELAY);
    scheduleCollapse(group);
  }

  function render(group) {
    if (!group || !group.stepper) return;
    // Open while the pointer is on it (desktop only) OR while we're inside the
    // hold-open window after a tap/add -- the latter is the mobile path. Before
    // the first add the slot stays a plain click-to-add cart icon.
    var until = openUntil.get(group.slot) || 0;
    var isOpen = hovering.has(group.slot) || Date.now() < until;
    if (group.cartQty > 0 && isOpen) showExpanded(group);
    else if (group.cartQty > 0) showCollapsed(group);
    else showAdd(group);
  }

  function applyCartState(cart) {
    // A rejected /cart/change.js call returns a {status, message} body, not a
    // real cart -- treating that as "everything is qty 0" would wrongly reset
    // every card. Bail out and let the caller re-sync from a real /cart.js.
    if (!cart || !Array.isArray(cart.items)) {
      document.querySelectorAll('[data-card-qty-stepper]').forEach(function (s) {
        s.classList.remove('is-loading');
      });
      return;
    }

    var qtyByVariant = {};
    cart.items.forEach(function (item) {
      qtyByVariant[item.variant_id] = (qtyByVariant[item.variant_id] || 0) + item.quantity;
    });

    allSlots().forEach(function (slot) {
      var group = groupFor(slot);
      if (!group.variantId) return;
      var qty = qtyByVariant[group.variantId] || 0;
      var prev = prevQty[group.variantId] || 0;
      group.cartQty = qty;
      if (group.stepper) {
        group.stepper.setAttribute('data-cart-qty', qty);
        group.stepper.classList.remove('is-loading');
      }
      paint(group);
      if (firstSyncDone && qty > prev) {
        // Just added / incremented -> open the stepper and hold it for
        // COLLAPSE_DELAY so the shopper has time to add more or remove. Works on
        // touch (no hover) as well as desktop.
        keepOpen(group);
      } else {
        render(group);
      }
      prevQty[group.variantId] = qty;
    });

    firstSyncDone = true;
    updateCartCountBubble(cart.item_count);
    updateFloatingCartNudge(cart.item_count);
  }

  function syncCartUI() {
    fetch(routeRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(applyCartState)
      .catch(function () {});
  }

  // First unit: /cart/add.js (change.js can't create a line). Re-sync afterwards
  // (add.js returns the line item, not the whole cart).
  function addFirstUnit(group) {
    if (group.stepper) group.stepper.classList.add('is-loading');
    fetch(routeRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: group.variantId, quantity: 1 })
    })
      .then(function (response) { return response.json().then(function (b) { return { ok: response.ok, body: b }; }); })
      .then(function () { if (group.stepper) group.stepper.classList.remove('is-loading'); syncCartUI(); })
      .catch(function () { if (group.stepper) group.stepper.classList.remove('is-loading'); });
  }

  function changeQuantity(group, quantity) {
    if (group.stepper) group.stepper.classList.add('is-loading');
    fetch(routeRoot() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: group.variantId, quantity: quantity })
    })
      .then(function (response) {
        return response.json().then(function (body) { return { ok: response.ok, body: body }; });
      })
      .then(function (result) {
        if (result.ok) applyCartState(result.body);
        else { if (group.stepper) group.stepper.classList.remove('is-loading'); syncCartUI(); }
      })
      .catch(function () { if (group.stepper) group.stepper.classList.remove('is-loading'); });
  }

  // ---------------------------------------------------------------------------
  // Interaction
  // ---------------------------------------------------------------------------
  document.addEventListener('click', function (event) {
    // Tapping the collapsed badge opens the stepper (touch devices have no hover).
    var badge = event.target.closest('[data-card-cart-badge]');
    if (badge) {
      event.preventDefault();
      var badgeGroup = groupFor(badge.closest('[data-card-atc-slot]'));
      // keepOpen (not hovering.add) -- on touch nothing would ever clear a faked
      // hover, so the stepper would stay open forever.
      if (badgeGroup) keepOpen(badgeGroup);
      return;
    }

    var decreaseBtn = event.target.closest('[data-qty-decrease]');
    var increaseBtn = event.target.closest('[data-qty-increase]');
    var button = decreaseBtn || increaseBtn;
    if (!button || button.disabled) return;

    var stepper = button.closest('[data-card-qty-stepper]');
    if (!stepper || stepper.classList.contains('is-loading')) return;

    event.preventDefault();
    var group = groupFor(stepper.closest('[data-card-atc-slot]'));
    if (!group) return;
    clearCollapse(group.slot); // don't collapse mid-interaction
    // Extend the hold-open window so a "-" tap (which isn't a qty increase)
    // doesn't immediately collapse the stepper on touch.
    openUntil.set(group.slot, Date.now() + COLLAPSE_DELAY);

    if (increaseBtn) {
      if (group.cartQty <= 0) addFirstUnit(group);
      else changeQuantity(group, group.cartQty + 1);
    } else {
      if (group.cartQty > 0) changeQuantity(group, group.cartQty - 1);
    }
  });

  // Hover expands the slot into the stepper; leaving it collapses again shortly
  // after. Delegated on document so AJAX-injected cards (tabbed sections, etc.)
  // get the behavior for free. Pointer devices ONLY -- on touch there is no
  // hover, and a synthetic mouseover would leave the stepper stuck open, so
  // phones use taps + the hold-open window instead.
  if (CAN_HOVER) {
    document.addEventListener('mouseover', function (event) {
      var slot = event.target.closest('[data-card-atc-slot]');
      if (!slot) return;
      hovering.add(slot);
      clearCollapse(slot);
      var group = groupFor(slot);
      // Only in-cart cards expand on hover; before the first add the cart icon
      // stays a plain click-to-add button.
      if (group && group.cartQty > 0 && group.stepper && group.stepper.hidden) showExpanded(group);
    });

    document.addEventListener('mouseout', function (event) {
      var slot = event.target.closest('[data-card-atc-slot]');
      if (!slot) return;
      // Ignore moves between the slot's own children.
      if (event.relatedTarget && slot.contains(event.relatedTarget)) return;
      hovering.delete(slot);
      var group = groupFor(slot);
      if (group) scheduleCollapse(group);
    });
  }

  // Re-sync every card's stepper whenever anything on the site adds, changes,
  // updates or clears the cart -- the PDP buy box, the mobile sticky bar, quick
  // shop, or this same card's own Add to Cart button.
  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isCartMutation = CART_MUTATION_PATTERN.test(url);
      return nativeFetch.apply(this, arguments).then(function (response) {
        if (isCartMutation) setTimeout(syncCartUI, 150);
        return response;
      });
    };
  }

  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (CART_MUTATION_PATTERN.test(url)) {
      this.addEventListener('loadend', function () {
        setTimeout(syncCartUI, 150);
      });
    }
    return nativeOpen.apply(this, arguments);
  };

  // Tabbed product sections (and other lazy grids) inject their cards over AJAX
  // after the initial sync, so a product already in the cart would otherwise
  // render as "not added" until the next cart mutation. Watch for new ATC slots
  // and re-sync (debounced) so those cards pick up their state at once.
  var resyncTimer = null;
  function scheduleResync() {
    if (resyncTimer) return;
    resyncTimer = setTimeout(function () { resyncTimer = null; syncCartUI(); }, 300);
  }

  function watchForNewSlots() {
    if (typeof MutationObserver !== 'function') return;
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if ((node.matches && node.matches('[data-card-atc-slot]')) ||
              (node.querySelector && node.querySelector('[data-card-atc-slot]'))) {
            scheduleResync();
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    syncCartUI();
    watchForNewSlots();
    repositionFloatingCartNudge();
    window.addEventListener('resize', repositionFloatingCartNudge);
    setTimeout(repositionFloatingCartNudge, 1000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
